import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from "../api";
import { RootState } from '../index';

// Define types for better type safety
interface FilterData {
  filters: Record<string, unknown>;
  page?: number;
  limit?: number;
}

// Async thunk for submitting search
export const submitSearch = createAsyncThunk(
  'search/submitSearch',
  async (formData: FilterData, { rejectWithValue }) => {
    try {

      // Helper function to check if a filter has a meaningful value
      const hasValue = (key: string, value: unknown): boolean => {
        if (value === undefined || value === null || value === '') return false;
        if (Array.isArray(value)) return value.length > 0;
        // "None" is the UI default for urgency; treat it as "no filter"
        if (key === 'urgencyLevel' && value === 'None') return false;
        if (typeof value === 'boolean') {
          // Optional booleans should only be sent when true to avoid
          // default false values becoming unintended hard filters.
          if (key === 'isActive') return true;
          return value === true;
        }
        if (typeof value === 'number') {
          // Avoid sending default year as an accidental hard filter
          if (key === 'maxYearLicensed' && value === new Date().getFullYear()) return false;
          return value > 0;
        }
        return true;
      };

      const asPositiveNumber = (value: unknown, fallback: number): number => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
      };

      // Build structured payload expected by matchmaking endpoint
      const query: Array<Record<string, unknown>> = [];

      // Get practice ID from localStorage (matching DashboardLayout logic)
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const practiceId = user?.['custom:id_practice'] || user?.practice_id;
      if (practiceId) {
        query.push({ id_practice: practiceId });
      }

      // Keep base scope strict and predictable
      query.push({ isActive: formData.filters.isActive ?? true });

      // ZIP code pattern — 5 digits, optionally +4
      const isZip = (v: string) => /^\d{5}(-\d{4})?$/.test(v.trim());

      // Split filters.location into city/state entries and ZIP entries,
      // then emit as two separate query objects so the backend can search
      // both the location field and the post_code field in parallel.
      const rawLocations = (formData.filters.location as string[] | undefined) ?? [];
      if (rawLocations.length > 0) {
        const cityStateEntries = rawLocations.filter(v => !isZip(v));
        const zipEntries       = rawLocations.filter(v =>  isZip(v));
        if (cityStateEntries.length > 0) query.push({ location: cityStateEntries });
        if (zipEntries.length       > 0) query.push({ post_code: zipEntries });
      }

      // Map remaining optional filters that have meaningful values
      const filterMappings = [
        'verificationStatus', 'billingStatus', 'state', 'city', 'zipCode',
        // 'location' intentionally omitted — handled above with post_code split
        'timezone', 'acceptingClients', 'minOpenSpots', 'urgencyLevel',
        'availableWeekdays', 'availableWeekends', 'underCapacity',
        'minTargetWeeklyClients', 'maxCurrentClients', 'clientTypes',
        'sessionTypes', 'specialties', 'treatmentModalities', 'languages',
        'hasMultipleLicenses', 'minYearLicensed', 'maxYearLicensed',
        'acceptsInsurance', 'insuranceProviders', 'maxIndividualRate', 'maxFamilyRate',
        // OpenSearch field names used by backend index
        'status', 'role', 'title', 'npiNumber', 'slug',
        'availability.open.date', 'availability.open.time'
      ];

      filterMappings.forEach(key => {
        if (hasValue(key, formData.filters[key])) {
          query.push({ [key]: formData.filters[key] });
        }
      });

      const payload = {
        index: 'clinicians',
        query,
        page: asPositiveNumber(formData.page ?? formData.filters.page, 1),
        limit: asPositiveNumber(formData.limit ?? formData.filters.limit, 50)
      };


      // Make request to the matchmaking endpoint
      const response = await api.post('/matchmaking', payload);


      // Parse the response body if it's a string
      let responseBody;
      try {
        const parseMaybeJson = (value: unknown): unknown => {
          if (typeof value !== 'string') return value;
          try {
            return JSON.parse(value);
          } catch {
            return value;
          }
        };

        // First parse: handles direct JSON, Lambda proxy body, and plain strings.
        const firstParsed = parseMaybeJson(response.data);

        // Unwrap Lambda proxy wrapper when present.
        // Cases supported:
        // 1) response.data = { statusCode, body: "{...}" }
        // 2) response.data = "{\"statusCode\":200,\"body\":\"{...}\"}"
        // 3) response.data = { ...actual payload... }
        const unwrapped = (
          firstParsed &&
          typeof firstParsed === 'object' &&
          'body' in (firstParsed as Record<string, unknown>)
        )
          ? parseMaybeJson((firstParsed as Record<string, unknown>).body)
          : firstParsed;

        responseBody = unwrapped;

        // Support multiple backend response shapes:
        // - { results: [...] }
        // - { data: [...] }
        // - { data: { items: [...] } }
        // - { items: [...] }
        const rawData =
          responseBody?.results ??
          responseBody?.data ??
          responseBody?.items ??
          responseBody;

        const normalizedData = Array.isArray(rawData)
          ? rawData
          : Array.isArray(rawData?.items)
            ? rawData.items
            : [];

        const pagination = responseBody?.pagination || {
          page: responseBody?.page ?? 1,
          limit: responseBody?.limit ?? 50,
          total: responseBody?.total ?? normalizedData.length,
          totalPages: responseBody?.totalPages
        };

        return {
          data: normalizedData,
          pagination
        };
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Failed to parse response data');
      }
    } catch (error: unknown) {
      console.error('Search submission error:', error);
      const errorMessage = error instanceof Error ? error.message : 'An error occurred during search';
      return rejectWithValue(errorMessage);
    }
  }
);

const searchSlice = createSlice({
  name: 'search',
  initialState: {
    isSubmitting: false,
    submitError: '',
    showSuccessModal: false,
    searchResults: null as unknown,
    lastSubmission: null as string | null
  },
  reducers: {
    clearSubmitError: (state) => {
      state.submitError = '';
    },
    setShowSuccessModal: (state, action) => {
      state.showSuccessModal = action.payload;
    },
    resetSearchState: (state) => {
      state.isSubmitting = false;
      state.submitError = '';
      state.showSuccessModal = false;
      state.searchResults = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitSearch.pending, (state) => {
        state.isSubmitting = true;
        state.submitError = '';
      })
      .addCase(submitSearch.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.searchResults = action.payload;
        state.showSuccessModal = true;
        state.lastSubmission = new Date().toISOString();
      })
      .addCase(submitSearch.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submitError = action.payload || 'Failed to submit search. Please try again.';
      });
  }
});

export const { clearSubmitError, setShowSuccessModal, resetSearchState } = searchSlice.actions;

export default searchSlice.reducer;

// Selectors
export const selectIsSubmitting = (state: RootState) => state.search.isSubmitting;
export const selectSubmitError = (state: RootState) => state.search.submitError;
export const selectShowSuccessModal = (state: RootState) => state.search.showSuccessModal;
export const selectSearchResults = (state: RootState) => state.search.searchResults;
export const selectLastSubmission = (state: RootState) => state.search.lastSubmission;
